import axios from 'axios'

export const CRM_JWT_ENABLED = process.env.VUE_APP_CRM_AUTH_MODE === 'jwt'

export function userToCrmSession(user, tokenData) {
  const { token, token_type: tokenType, expires_at: expiresAt, expires_in: expiresIn } = tokenData
  // 檢查 Unix 秒數與到期；最長有效時間由後端控制，避免電腦稍慢就誤判超期。
  if (typeof token !== 'string' || !token || /\s/.test(token) || tokenType !== 'Bearer' ||
      !Number.isInteger(expiresAt) || expiresAt <= Date.now() / 1000 || expiresAt > 9999999999 ||
      !user || !['1', '2'].includes(user.type) || typeof user.kind !== 'string' ||
      !['0', '99'].includes(user.plan) || !['0', '1'].includes(String(user.is_root)) ||
      !(user.crm_user_id === null || (typeof user.crm_user_id === 'string' && user.crm_user_id.length > 0)) ||
      !Array.isArray(user.permissions) || !Array.isArray(user.route_permissions) ||
      ['clinic_code', 'clinic_name', 'doctor_code', 'doctor_name'].some(key => user[key] != null && typeof user[key] !== 'string')) {
    throw new Error('新版登入回應格式不完整，請聯絡工程人員')
  }
  const session = {
    auth_mode: 'jwt', token, token_type: tokenType, expires_at: expiresAt, expires_in: expiresIn,
    type: user.type, kind: user.kind, plan: user.plan,
    clinic_code: user.clinic_code ?? '', clinic_name: user.clinic_name ?? '',
    doctor_code: user.doctor_code ?? '', doctor_name: user.doctor_name ?? '',
    is_root: String(user.is_root), permissions: [...user.permissions], route_permissions: [...user.route_permissions],
  }
  if (user.crm_user_id !== null) {
    session.crm_user_id = user.crm_user_id
    session.crm_user_account = user.account ?? ''
    session.crm_user_name = user.name ?? ''
  }
  return session
}

export function normalizeCrmLogin(data) {
  return userToCrmSession(data && data.user, {
    token: data && data.access_token, token_type: data && data.token_type,
    expires_at: data && data.expires_at, expires_in: data && data.expires_in,
  })
}

export function normalizeCrmMe(data, requestToken, nowSeconds = Math.floor(Date.now() / 1000)) {
  return userToCrmSession(data && data.user, {
    token: requestToken, token_type: 'Bearer', expires_at: data && data.expires_at,
    expires_in: data && Math.max(0, data.expires_at - nowSeconds),
  })
}

export function normalizeCrmSwitch(data) {
  return userToCrmSession(data && {
    ...data, crm_user_id: data.crm_user_id ?? null, account: data.crm_user_account, name: data.crm_user_name,
  }, { token: data && data.token, token_type: data && data.token_type,
    expires_at: data && data.expires_at, expires_in: data && data.expires_in })
}

// 隔離全域 axios 的 bearer／操作者參數及 interceptor；每個請求明確指定授權。
export function createCrmAuthClient() {
  const client = axios.create({ baseURL: process.env.VUE_APP_API_URL || axios.defaults.baseURL, withCredentials: false, timeout: 15000 })
  delete client.defaults.params
  for (const key of Object.keys(client.defaults.headers)) {
    if (key.toLowerCase() === 'authorization') delete client.defaults.headers[key]
    else if (client.defaults.headers[key] && typeof client.defaults.headers[key] === 'object') {
      for (const header of Object.keys(client.defaults.headers[key])) {
        if (header.toLowerCase() === 'authorization') delete client.defaults.headers[key][header]
      }
    }
  }
  // 與全域 axios 相同帶前端版本號（同一個 VUE_APP_BUILD_ID）；這個 client 只打 CRM 後端。
  const build = process.env.VUE_APP_BUILD_ID || ''
  if (build) client.defaults.headers.common = { ...client.defaults.headers.common, 'X-CRM-Build': build }
  return client
}

let onInvalidCrmToken = () => {}
export function setCrmAuthInvalidHandler(handler) { onInvalidCrmToken = handler }

async function authorizedCrmRequest(send, bearer) {
  try { return await send() } catch (error) {
    if (error.response && error.response.status === 401 && error.response.data && error.response.data.error &&
        error.response.data.error.code === 'invalid_token') onInvalidCrmToken(bearer)
    throw error
  }
}

export const crmAuthHttp = {
  post(path, body, options) {
    const bearer = axios.defaults.headers.common.Authorization
    return authorizedCrmRequest(() => createCrmAuthClient().post(path, body, {
      ...options, headers: { Authorization: bearer },
    }), bearer)
  },
}

export async function loginCrmJwt(credentials, http = createCrmAuthClient()) {
  const { account, password, type } = credentials
  const response = await http.post('/api/external-auth/login', { account, password, type }, { timeout: 15000, headers: { Authorization: undefined } })
  return { ...response, data: normalizeCrmLogin(response.data) }
}

export async function getCrmSession(requestToken, http = createCrmAuthClient()) {
  const bearer = `Bearer ${requestToken}`
  const response = await authorizedCrmRequest(() => http.get('/api/external-auth/me', {
    headers: { Authorization: bearer }, timeout: 15000,
  }), bearer)
  return normalizeCrmMe(response.data, requestToken)
}

export async function refreshCrmSession(previous, getCurrent, apply, http) {
  const session = await getCrmSession(previous.token, http)
  if (getCurrent() !== previous) return false
  if (requiresCrmLogin(session, true)) throw new Error('帳號的醫療類別或登入期限不完整')
  apply(session)
  return true
}

export function requiresCrmLogin(user, useJwt = CRM_JWT_ENABLED, now = Date.now()) {
  if (!user || !user.kind) return true
  if (user.auth_mode === 'jwt') return !user.token || !Number.isInteger(user.expires_at) || user.expires_at * 1000 <= now
  return useJwt
}

export function crmLoginError(error) {
  const status = error.response && error.response.status
  const code = error.response && error.response.data && error.response.data.error && error.response.data.error.code
  if (status === 401) return '帳號或密碼不正確，或帳號已停用'
  if (status === 429) return '登入次數過多，請稍候一分鐘再試'
  if (status === 503) return '新版登入服務尚未就緒，請稍後再試'
  if (status === 422) return '請確認帳號、密碼與登入類型'
  if (code || status) return '登入失敗，請稍後再試'
  if (error.code === 'ECONNABORTED') return '登入逾時，請稍後再試'
  return '暫時無法登入，請確認連線或聯絡工程人員'
}
